import { Component, OnInit, Renderer2 } from '@angular/core';

import QRCode from 'qrcode-generator';
import { ActivatedRoute } from '@angular/router';
import { finalize, Subject, takeUntil } from 'rxjs';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { AesEncryptionService } from '@services/aes-encryption.service';
import { EventBookingDetails } from '@/modals/event-details/eventBookingDetails';
import { AudienceDetails } from '@/modals/workshop-details/audienceDetails';

@Component({
    selector: 'app-ticket',
    templateUrl: './ticket.component.html',
    styleUrls: ['./ticket.component.scss']
})
export class TicketComponent implements OnInit {
    destroy$: Subject<boolean> = new Subject<boolean>();
    currentYear = new Date().getFullYear();
    qrCodeImg: string = "";
    ticketSeq: string = "";
    ticketDetails: any;
    eventDetails: EventBookingDetails[] = [];
    errorMessage: string = "";
    hostName: string = "";
    getTicketDetails: AudienceDetails [] = [];
    searchText: any;
    constructor(
        private fb: FormBuilder,
        private appService: AppService,
        private spinner: NgxSpinnerService,
        private toastr: ToastrService,
        private renderer: Renderer2,
        private route: ActivatedRoute,
        private aesService: AesEncryptionService
    ) { }

    eventForm = this.fb.group({
        whatsappNumber: ['', [Validators.required, Validators.pattern(/^[6-9][0-9]{9}$/)]],
        eventId: ["", Validators.required]
    })

    ngOnInit(): void {
        this.eventDetailsGetAll();
        this.hostName = window.location.hostname;
    }

    eventDetailsGetAll() {
  this.spinner.show();

  this.appService.eventDetailsGetAll()
    .pipe(takeUntil(this.destroy$))
    .subscribe((data) => {

      // 🔹 Today start in IST
      const now = new Date();
      const todayIST = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0, 0, 0, 0
      ).getTime();

      const upcomingEvents = data
        .map(e => ({
          ...e,
          eventDate: Number(e.eventDate)   // 🔹 ensure number
        }))
        .filter(e => e.eventDate >= todayIST) // 🔹 filter based on eventDate
        .sort((a, b) => a.eventDate - b.eventDate);

      this.eventDetails = upcomingEvents;
      if (this.eventDetails.length === 1) {
        this.eventForm.get('eventId')?.setValue(String(this.eventDetails[0]?.eventId));
      } else {
        // Keep placeholder
        this.eventForm.get('eventId')?.setValue('');
      }
      this.spinner.hide();

    }, (error) => {
      this.spinner.hide();
      this.toastr.error(error?.error?.message || "Something Went Wrong, Please Try Again");
    });
}




    // Common phone handler for BOTH phoneNumber & whatsappNumber
    onPhoneInput(event: Event, controlName: string) {
        const input = event.target as HTMLInputElement;
        let val = input.value.replace(/\D/g, '');

        // must start with 6-9
        if (val.length > 0 && !/^[6-9]/.test(val)) {
            val = val.substring(1);
        }

        // limit 10 digits
        val = val.substring(0, 10);

        input.value = val;
        this.eventForm.get(controlName)?.setValue(val, { emitEvent: false });
    }

    // Paste handler
    onPhonePaste(event: ClipboardEvent, controlName: string) {
        event.preventDefault();
        let val = (event.clipboardData?.getData("text") || "").replace(/\D/g, "").substring(0, 10);

        if (val.length > 0 && !/^[6-9]/.test(val)) {
            val = val.replace(/^[0-5]+/, "");
        }

        const input = event.target as HTMLInputElement;
        input.value = val;

        this.eventForm.get(controlName)?.setValue(val, { emitEvent: false });
    }


    allowOnlyDigits(event: KeyboardEvent) {
        if (!/^[0-9]$/.test(event.key) && event.key.length === 1) {
            event.preventDefault();
        }
    }

    getMyTicketById() {
        this.spinner.show();
        this.appService.getTicketById(this.ticketSeq)
            .pipe(finalize(() => this.spinner.hide()))
            .subscribe({
                next: async (data) => {
                    console.log(data);

                    this.ticketDetails = data;

                    // WAIT for QR to finish generating
                    this.qrCodeImg = await this.generateQRCode(this.ticketSeq);
                },

                error: (err) => this.toastr.error(err.error?.message || err.message)
            });
    }

    // 🔹 generate QR + transparent background
    async generateQRCode(ticketNo: string): Promise<string> {
        const qr = QRCode(0, 'M');
        qr.addData(ticketNo);
        qr.make();

        const cellSize = 4;
        const margin = 0;

        const dataURL = qr.createDataURL(cellSize, margin);

        return await this.convertWhiteToTransparent(dataURL);
    }

    // 🔹 convert white → transparent asynchronously
    private convertWhiteToTransparent(dataUrl: string): Promise<string> {
        return new Promise((resolve) => {
            const img = new Image();
            img.src = dataUrl;

            img.onload = () => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d")!;

                canvas.width = img.width;
                canvas.height = img.height;

                ctx.drawImage(img, 0, 0);

                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const data = imageData.data;

                for (let i = 0; i < data.length; i += 4) {
                    const r = data[i];
                    const g = data[i + 1];
                    const b = data[i + 2];

                    // Black pixels → WHITE
                    if (r < 50 && g < 50 && b < 50) {
                        data[i] = 255;     // R
                        data[i + 1] = 255; // G
                        data[i + 2] = 255; // B
                        data[i + 3] = 255; // Opaque
                    }

                    // White pixels → Transparent
                    if (r > 240 && g > 240 && b > 240) {
                        data[i + 3] = 0; // transparent
                    }
                }

                ctx.putImageData(imageData, 0, 0);
                resolve(canvas.toDataURL("image/png"));
            };
        });
    }

     getSubParticipantsNames(audience: any[]): string {
    if (!audience || audience.length <= 1) {
      return "-"; // No sub participants
    }

    // Skip the first participant and return others
    const subNames = audience.slice(1).map(a => a.participantName);

    return subNames.join(", "); // join with comma
  }


    onSearch() {
        this.errorMessage = "";
        this.getTicketDetails = [];
        this.ticketDetails = null;
        if (this.eventForm.invalid) {
            this.eventForm.markAllAsTouched();
            return;
        }

        const body = {
            eventId: this.eventForm.value.eventId,
            whatsappNumber: this.eventForm.value.whatsappNumber
        };

        this.spinner.show();

        this.appService.searchParticipants(body)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                async (data) => {
                    this.spinner.hide();

                    if (!data || data.length === 0) {
                        this.toastr.error("பதிவு செய்யப்படவில்லை");
                        this.ticketDetails = null;
                        return;
                    }
                    this.getTicketDetails = data;
                    this.ticketDetails = data[0];
                    this.ticketSeq = this.ticketDetails.ticketSeq;

                    // QR generation
                    this.qrCodeImg = await this.generateQRCode(this.ticketSeq);

                },
                (error) => {
                    this.spinner.hide();
                    if (error.status === 404) {
                        this.errorMessage = "EVENT_NOT_REGISTERED";  // show custom message
                    } else {
                        this.errorMessage = "error.error?.message";
                        this.toastr.error(error.error?.message || "Something Went Wrong");
                    }
                }
            );
    }

    getTicketUrl(ticketSeq: string) {
    const encrypted = this.aesService.encryptTicket(ticketSeq);
    const encoded = encodeURIComponent(encrypted);

    // Default URL (production)
    let baseUrl = 'https://ticket.velmaralsevatrust.org/index.html';

    // For local & booking environments use cktechsoln ticket portal
    if (this.hostName === 'localhost' || this.hostName === 'booking.cktechsoln.in') {
      baseUrl = 'https://ticket.cktechsoln.in/index.html';
    }

    // For ticket.cktechsoln.in host use velmaral domain
    if (this.hostName === 'ticket.cktechsoln.in') {
      baseUrl = 'https://ticket.velmaralsevatrust.org/index.html';
    }

    return `${baseUrl}?ticketNo=${encoded}`;
  }

}
